import React from "react"
import PropTypes from "prop-types"
import { Card, CardBody, CardHeader, Badge } from "reactstrap"
import { useEvaluationFormLogic } from "../Hooks/useEvaluationFormLogic"
import { MemberSearch } from "../../../components/Common"
import TechnicalEvaluationForm from "./TechnicalEvaluationForm"
import TestEvaluationForm from "./TestEvaluationForm"

const EvaluationCard = ({ schedule, activeMode = "technical" }) => {
  const internalActiveTab = activeMode === "technical" ? "1" : "2"

  const {
    color,
    activityName,
    employeeName,
    startTime,
    endTime,
    isEvaluationDisabled,
  } = schedule || {}

  const evaluationLogic = useEvaluationFormLogic({ classId: schedule?.idClass, activeMode })

  const {
    formReady,
    searchText,
    setSearchText,
    activeEvent,
    activeTestEvent,
    addCandidates,
    showNoAutocompleteResults,
    handleAddMember,
  } = evaluationLogic

  return (
    <Card className="h-100 shadow-sm border-0 bg-light" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {schedule ? (
        <CardHeader className="bg-white px-4 py-4 border-bottom-0 shadow-sm position-relative z-1">
          {color && <div className="position-absolute top-0 start-0 w-100" style={{ height: '4px', backgroundColor: color }}></div>}

          <div className="d-flex flex-column gap-3">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Badge color={activeMode === 'technical' ? 'primary' : 'info'} className="px-2 py-1 fw-medium" style={{ fontSize: '11px' }}>
                    {activeMode === 'technical' ? 'AVALIAÇÃO TÉCNICA' : 'TESTES DE PERFORMANCE'}
                  </Badge>
                </div>
                <h3 className="mb-2 fw-bold text-dark" style={{ letterSpacing: '-0.5px' }}>{activityName || "Evento"}</h3>
                <div className="d-flex align-items-center gap-3">
                  {employeeName && (
                    <div className="d-flex align-items-center gap-1 text-muted fw-medium font-size-13">
                      <i className="mdi mdi-account-circle-outline fs-5" />
                      <span>{employeeName}</span>
                    </div>
                  )}
                  <div className="d-flex align-items-center gap-1 text-muted fw-medium font-size-13 bg-light px-2 py-1 rounded-0">
                    <i className="mdi mdi-clock-outline text-primary" />
                    <span>{startTime} — {endTime}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-100 mt-2">
              <MemberSearch
                value={searchText}
                onChange={setSearchText}
                disabled={!formReady || isEvaluationDisabled}
                candidates={addCandidates}
                onSelect={handleAddMember}
                showNoResults={showNoAutocompleteResults}
                noResultsLabel="Nenhum membro encontrado com contrato ativo para adicionar."
              />
            </div>
          </div>
        </CardHeader>
      ) : (
        <CardHeader className="bg-white px-4 py-4 border-bottom-0 shadow-sm position-relative z-1">
          <div className="d-flex flex-column gap-3">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Badge color={activeMode === 'technical' ? 'primary' : 'info'} className="px-2 py-1 fw-medium" style={{ fontSize: '11px' }}>
                    {activeMode === 'technical' ? 'AVALIAÇÃO TÉCNICA' : 'TESTES DE PERFORMANCE'}
                  </Badge>
                </div>
                <h3 className="mb-2 fw-bold text-dark" style={{ letterSpacing: '-0.5px' }}>Painel de {activeMode === 'technical' ? 'Avaliação' : 'Testes'}</h3>
                <div className="d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center gap-1 text-muted fw-medium font-size-13 bg-light px-2 py-1 rounded-0">
                    <i className="mdi mdi-information-outline text-muted" />
                    <span>Nenhuma turma selecionada</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-100 mt-2">
              <MemberSearch
                value={searchText}
                onChange={setSearchText}
                disabled={true}
                candidates={[]}
                onSelect={() => { }}
                showNoResults={false}
                noResultsLabel="Nenhum membro"
              />
            </div>
          </div>
        </CardHeader>
      )}

      <CardBody className="p-0 d-flex flex-column" style={{ overflow: 'hidden' }}>
        <div className="h-100 overflow-auto bg-white" style={{ minHeight: '400px' }}>
          {!schedule ? (
            <div className="p-4 p-md-5 d-flex flex-column h-100">
              {/* Global Active Event Info (Shows when no class is selected) */}
              {activeMode === "performance" ? (
                activeTestEvent ? (
                  <div className="bg-white p-4 rounded-0 shadow-sm border border-info border-opacity-25 position-relative overflow-hidden">
                    <div className="position-absolute top-0 start-0 w-100 h-100 bg-info bg-opacity-10" style={{ pointerEvents: 'none' }}></div>
                    <div className="d-flex align-items-start gap-3 position-relative z-1">
                      <div className={`flex-shrink-0 text-info`}>
                        <i className={`mdi ${(['fixed_time', 'distance'].includes(activeTestEvent?.testType)) ? 'mdi-timer-sand' : 'mdi-run-fast'} fs-2`}></i>
                      </div>
                      <div>
                        <Badge color="info" className="mb-2 fw-medium px-2 py-1">Teste de Performance Ativo</Badge>
                        <h5 className="mb-1 fw-bold text-dark">{activeTestEvent.name}</h5>
                        <div className="text-muted font-size-13">
                          <span className="fw-medium text-dark">Regra: </span>
                          {(['fixed_time', 'distance'].includes(activeTestEvent?.testType))
                            ? `Tempo Fixo (${activeTestEvent?.testConfig?.referenceValue || 0}${activeTestEvent?.testConfig?.unit || ''}) - Medir Distância do Aluno`
                            : `Distância Fixa (${activeTestEvent?.testConfig?.referenceValue || 0}${activeTestEvent?.testConfig?.unit || ''}) - Medir Tempo do Aluno`
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white p-4 rounded-0 shadow-sm border border-warning border-opacity-50">
                    <div className="d-flex align-items-start gap-3">
                      <div className="flex-shrink-0 text-warning">
                        <i className="mdi mdi-alert-circle-outline fs-2"></i>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold text-dark">Nenhum Teste Agendado</h5>
                        <div className="text-muted font-size-13">
                          Não há provas ou testes de performance com prazos ativos no momento.
                        </div>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                activeEvent ? (
                  <div className="bg-white p-4 rounded-0 shadow-sm border border-primary border-opacity-25 position-relative overflow-hidden">
                    <div className="position-absolute top-0 start-0 w-100 h-100 bg-primary bg-opacity-10" style={{ pointerEvents: 'none' }}></div>
                    <div className="d-flex align-items-start gap-3 position-relative z-1">
                      <div className="flex-shrink-0 text-primary">
                        <i className="mdi mdi-star-circle-outline fs-2"></i>
                      </div>
                      <div>
                        <Badge color="primary" className="mb-2 fw-medium px-2 py-1">Avaliação Técnica Ativa</Badge>
                        <h5 className="mb-1 fw-bold text-dark">{activeEvent.name}</h5>
                        <div className="text-muted font-size-13">
                          Período de graduações e exames de faixa em andamento.
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white p-4 rounded-0 shadow-sm border border-warning border-opacity-50">
                    <div className="d-flex align-items-start gap-3">
                      <div className="flex-shrink-0 text-warning">
                        <i className="mdi mdi-alert-circle-outline fs-2"></i>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold text-dark">Nenhuma Avaliação Agendada</h5>
                        <div className="text-muted font-size-13">
                          Não existem ciclos de avaliação técnica com prazos ativos no momento.
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}

              <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center text-center mt-5">
                <div className={`rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-4 ${activeMode === 'performance' ? 'text-info' : 'text-primary'}`} style={{ width: 80, height: 80 }}>
                  <i className={activeMode === 'performance' ? "mdi mdi-timer-sand" : "mdi mdi-text-box-search-outline"} style={{ fontSize: '40px' }} />
                </div>
                <h4 className="text-dark fw-bold mb-2">Painel de {activeMode === 'performance' ? 'Testes' : 'Avaliação'}</h4>
                <p className="text-muted font-size-15" style={{ maxWidth: '300px' }}>Selecione uma turma na lista lateral para visualizar e avaliar os alunos.</p>
              </div>
            </div>
          ) : (
            <div className="p-3 p-md-4">
              {internalActiveTab === "1" && (
                <TechnicalEvaluationForm
                  key={`tech_${schedule.idClass}_${schedule.idActivity}_${schedule.startTime}`}
                  classId={schedule.idClass}
                  idActivity={schedule.idActivity}
                  classEmployeeName={schedule.employeeName || ""}
                  activityName={schedule.activityName || ""}
                  evaluationLogic={evaluationLogic}
                />
              )}

              {internalActiveTab === "2" && (
                <TestEvaluationForm
                  key={`test_${schedule.idClass}_${schedule.idActivity}_${schedule.startTime}`}
                  idActivity={schedule.idActivity}
                  evaluationLogic={evaluationLogic}
                />
              )}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  )
}

EvaluationCard.propTypes = {
  schedule: PropTypes.shape({
    activityName: PropTypes.string,
    employeeName: PropTypes.string,
    areaName: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    maxCapacity: PropTypes.number,
    enrolledCount: PropTypes.number,
    attendanceRecorded: PropTypes.bool,
    presentCount: PropTypes.number,
    absentCount: PropTypes.number,
    color: PropTypes.string,
    idClass: PropTypes.string,
    idActivity: PropTypes.string,
  })
}

export default EvaluationCard
